<template>
  <!-- eslint-disable max-len -->
  <div class="modal-parent">
    <transition name="fade">
      <confirmation-modal
        v-if="showConfirmModal"
        :confirmation-type="confirmation.type"
        :title="confirmation.title"
        :description="confirmation.description"
        :button-text="confirmation.button"
        @confirm="confirmAction"
        @cancel="toggleConfirmModal"
      />
    </transition>
    <div
      v-show="!showConfirmModal"
      v-shortkey="['esc']"
      class="modal"
      @shortkey="closeModal"
    >
      <div class="modal_topbar modal_topbar--no-actions">
        <div class="modal_topbar-title modal_topbar-title--no-actions">
          {{ modalTitle }}: {{ currentStepTitle }}
        </div>
        <div class="modal_topbar_action modal_topbar_action--no-actions">
          <template v-if="loading || fetching">
            <loader :size="25" />
          </template>
        </div>
        <span
          id="exit-group-modal"
          class="modal_topbar-exit"
          @click="closeModal"
          v-html="closeIcon"
        />
      </div>
      <div v-show="currentStep === 0 && !hasLimitedPermissionsToUpdateGroup">
        <div class="form-container">
          <label class="form-label">Group Name</label>
          <input
            id="name-group-modal"
            v-model="groupDetails.name"
            v-focus
            v-tooltip="{
              content: 'Valid characters: a-z, A-Z, 0-9',
              placement: 'top-end',
              arrow: false,
              trigger: 'mouseenter click focus',
            }"
            class="form-field"
            type="text"
          />
        </div>
        <div class="form-container">
          <label class="form-label">Description</label>
          <input
            id="description-group-modal"
            v-model="groupDetails.description"
            class="form-field"
            type="text"
          />
        </div>
        <div class="form-container">
          <label class="form-label">Group Mobile Verification</label>
          <select
            id="authenticator-dropdown"
            v-model="groupAuthenticator"
            v-tooltip="{
              content:
                'Select if a Mobile verification will be active for Group. Only available if authenticator configured in Account Configuration',
            }"
            class="form-field select-dropdown"
          >
            <option
              v-for="authenticator in authenticators"
              :key="authenticator.uuid"
              :value="authenticator"
            >
              {{ authenticator.name }} ({{ authenticator.type | uppercase }})
            </option>
          </select>
        </div>
        <p class="mt-2">
          List any email addresses which will receive device access alerts for this group
        </p>
        <div class="form-container">
          <vue-tags-input
            id="emails-group-modal"
            v-model="alertEmailInput"
            :add-on-key="[13, ',', ' ']"
            :max-tags="3"
            :placeholder="'Add up to three emails...'"
            :validation="tagInput.validation"
            :tags="alertEmailsInput"
            :separators="[',']"
            @tags-changed="emailsChanged"
          />
        </div>
        <div class="modal_footer modal_footer--right">
          <div class="error">
            {{ errorMessage }}
          </div>
          <button
            id="continue-group-modal-0"
            class="btn"
            @click="toNextStep()"
          >
            Continue
          </button>
        </div>
      </div>
      <div v-show="currentStep === 1">
        <div class="modal__description">
          Select the users you would like to assign to {{ groupDetails.name }}.
        </div>
        <select-from-list
          :label="'User'"
          :list-items="users"
          :total-rows="totalUserRows"
          :pagination-params.sync="userPaginationParams"
          :search.sync="userSearchTerm"
          :table-headers="userTableHeaders"
          :selected-items.sync="selectedUsers"
          :test-label="'username'"
        >
          <template v-slot:empty>
            <div class="empty-state">
              {{
                fetching
                  ? "Fetching Users..."
                  : "Could not find any users. Users can be added in User Management."
              }}
            </div>
          </template>
        </select-from-list>
        <div
          class="modal_footer"
          :class="{ 'modal_footer--right': hasLimitedPermissionsToUpdateGroup }"
        >
          <button
            v-if="!hasLimitedPermissionsToUpdateGroup"
            id="back-group-modal-1"
            class="btn btn--grey"
            @click="toBackStep()"
          >
            Back
          </button>
          <button
            id="continue-group-modal-1"
            class="btn"
            @click="toNextStep()"
          >
            Continue
          </button>
        </div>
      </div>
      <div v-show="currentStep === 2 && !hasLimitedPermissionsToUpdateGroup">
        <div class="modal__description">
          <div>
            Select devices to add them to this group. Adding a device to this group will
            <strong>remove</strong> it from any other group it is currently in.
          </div>
        </div>
        <select-from-list
          :label="'Device'"
          :list-items="devices"
          :total-rows="totalDeviceRows"
          :pagination-params.sync="devicePaginationParams"
          :search.sync="deviceSearchTerm"
          :table-headers="deviceTableHeaders"
          :selected-items.sync="selectedDevices"
        >
          <template v-slot:empty>
            <div class="empty-state">
              {{
                fetching
                  ? "Fetching Devices..."
                  : "Could not find any devices. Devices can be added in Device Management."
              }}
            </div>
          </template>
        </select-from-list>
        <div class="modal_footer">
          <button
            id="back-group-modal-2"
            class="btn btn--grey"
            @click="toBackStep()"
          >
            Back
          </button>
          <button
            id="continue-group-modal-2"
            class="btn"
            @click="toNextStep()"
          >
            Continue
          </button>
        </div>
      </div>
      <div v-show="currentStep === 3">
        <div class="modal__description">
          Only the users within this group will have access to these devices.
        </div>
        <div class="group-list-container">
          <div class="form-container">
            <label class="form-label">Group Name</label>
            {{ groupDetails.name }}
          </div>
          <div class="form-container">
            <label class="form-label">Description</label>
            {{ groupDetails.description }}
          </div>
          <div class="form-container">
            <label class="form-label">Group Authenticator</label>
            {{ groupAuthenticator.name }} ({{ groupAuthenticator.type }})
          </div>
          <div class="form-container m-0">
            <label class="form-label">Alert Emails</label>
            {{ alertEmailsString }}
          </div>
        </div>
        <div class="flex-grid">
          <div
            v-show="addedUsers.length > 0"
            class="column--two group-list-container"
          >
            <div class="form-label">
              Users to be added
            </div>
            <ul class="group-list">
              <li
                v-for="(user, index) in addedUsers"
                :key="index"
                class="group-list-item"
              >
                {{ user.username }}
                <span
                  v-tooltip="{ content: 'Remove' }"
                  class="group-list-item-remove"
                  @click="deselectListUser(user)"
                  v-html="removeIcon"
                />
              </li>
            </ul>
          </div>
          <div
            v-show="deletedUsers.length > 0"
            class="column--two group-list-container"
          >
            <div class="form-label">
              Users to be deleted
            </div>
            <ul class="group-list">
              <li
                v-for="(user, index) in deletedUsers"
                :key="index"
                class="group-list-item"
              >
                {{ user.username }}
                <span
                  v-tooltip="{ content: 'Remove' }"
                  class="group-list-item-remove"
                  @click="deselectListUser(user)"
                  v-html="removeIcon"
                />
              </li>
            </ul>
          </div>
          <div
            v-show="addedDevices.length > 0"
            class="column--two group-list-container"
          >
            <div class="form-label">
              Devices to be added
            </div>
            <ul class="group-list">
              <li
                v-for="device in addedDevices"
                :key="device.serialNumber"
                class="group-list-item"
              >
                {{ device.name }}
                <span
                  v-tooltip="{ content: 'Remove' }"
                  class="group-list-item-remove"
                  @click="deselectListDevice(device)"
                  v-html="removeIcon"
                />
              </li>
            </ul>
          </div>
          <div
            v-show="deletedDevices.length > 0"
            class="column--two group-list-container"
          >
            <div class="form-label">
              Devices to be deleted
            </div>
            <ul class="group-list">
              <li
                v-for="device in deletedDevices"
                :key="device.serialNumber"
                class="group-list-item"
              >
                {{ device.name }}
                <span
                  v-tooltip="{ content: 'Remove' }"
                  class="group-list-item-remove"
                  @click="deselectListDevice(device)"
                  v-html="removeIcon"
                />
              </li>
            </ul>
          </div>
        </div>
        <div class="modal_footer">
          <button
            id="back-group-modal-3"
            class="btn btn--grey"
            @click="toBackStep()"
          >
            Back
          </button>
          <button
            id="continue-group-modal-3"
            class="btn"
            :disabled="loading"
            type="submit"
            @click="submissionStart"
          >
            {{ modalTitle }}
          </button>
        </div>
      </div>
    </div>
    <div class="modal-overlay" />
  </div>
  <!-- eslint-enable max-len -->
</template>

<script>
import feather from 'feather-icons'
import VueTagsInput from '@johmun/vue-tags-input'
// Queries
import credentialQueries from '@/graphql/credentials.js'
import userQueries from '@/graphql/users.js'
import deviceQueries from '@/graphql/devices.js'

import Loader from './loader'
import SelectFromList from '@/components/selectFromList.vue'
import ConfirmationModal from '@/components/confirmationModal.vue'

import REGEX from '@/global/utilities.js'

export default {
  name: 'AddGroup',
  components: {
    loader: Loader,
    SelectFromList,
    VueTagsInput,
    ConfirmationModal,
  },
  mixins: [userQueries, deviceQueries, credentialQueries],
  props: ['group', 'loading', 'error', 'updateMode'],
  data() {
    return {
      fetching: false,
      currentStep: 0,
      errorMessage: '',
      alertEmailInput: '',
      alertEmailsInput: [],
      groupAuthenticator: {
        name: 'Bypass',
        type: 'none',
        uuid: null,
      },
      authenticators: [],
      userSearchTerm: '',
      users: [],
      selectedUsers: [],
      totalUserRows: 0,
      userPaginationParams: {
        page: 1,
        perPage: 10,
      },
      userTableHeaders: [
        {
          label: 'Username',
          prop: 'username',
        },
        {
          label: 'First Name',
          prop: 'firstName',
        },
        {
          label: 'Last Name',
          prop: 'lastName',
        },
        {
          label: 'Current Group',
          prop: 'groupNames',
        },
      ],
      deviceSearchTerm: '',
      devices: [],
      selectedDevices: [],
      totalDeviceRows: 0,
      devicePaginationParams: {
        page: 1,
        perPage: 10,
      },
      deviceTableHeaders: [
        {
          label: 'Device Name',
          prop: 'name',
        },
        {
          label: 'Current Group',
          prop: 'groupName',
        },
      ],
      groupDetails: {
        uuid: '',
        name: '',
        description: '',
      },
      tagInput: {
        validation: [
          {
            classes: 'not-email',
            rule: REGEX.email,
          },
        ],
      },
      closeIcon: feather.icons.x.toSvg(),
      removeIcon: feather.icons.x.toSvg({width: '1em'}),
      hasLimitedPermissionsToUpdateGroup: false,
      showConfirmModal: false,
      confirmation: {
        type: 'friendly',
        title: 'Device Conflict',
        button: 'Add Here',
        description: '',
      },
    }
  },
  computed: {
    modalTitle() {
      return this.updateMode ? 'Update Group' : 'Add Group'
    },
    addedUsers() {
      if (this.updateMode) {
        return this.addedItems(this.group.users, this.selectedUsers, 'username')
      }
      return this.selectedUsers
    },
    deletedUsers() {
      if (this.updateMode) {
        return this.deletedItems(this.group.users, this.selectedUsers, 'username')
      }
      return []
    },
    addedDevices() {
      if (this.updateMode) {
        return this.addedItems(this.group.devices, this.selectedDevices, 'serialNumber')
      }
      return this.selectedDevices
    },
    deletedDevices() {
      if (this.updateMode) {
        return this.deletedItems(this.group.devices, this.selectedDevices, 'serialNumber')
      }
      return []
    },
    alertEmails() {
      if (this.alertEmailsInput && this.alertEmailsInput.length > 0) {
        return this.alertEmailsInput
          .filter((input) => {
            return input.tiClasses.includes('ti-valid')
          })
          .map((email) => {
            return email.text
          })
      }
      return []
    },
    alertEmailsString() {
      if (this.alertEmails.length > 0) {
        return this.alertEmails.join(', ')
      }
      return 'No assigned emails.'
    },
    currentStepTitle() {
      const _title = ['Details', 'Edit Users', 'Edit Devices', 'Review']
      return _title[this.currentStep]
    },
  },
  watch: {
    deviceSearchTerm() {
      this.setDeviceSearch()
    },
    userSearchTerm() {
      this.setUserSearch()
    },
    userPaginationParams: {
      handler: function () {
        this.fetchUsers()
      },
      deep: true,
    },
    devicePaginationParams: {
      handler: function () {
        this.fetchDevices()
      },
      deep: true,
    },
  },
  /*
   * On initialization if the modal is set to be updated (an existing group
   * was selected) we set the group details to the selected device and
   * set the selectedUsers/devices to a copy of the selected group's original
   * users/devices. To avoid copying the reference and preventing the object to
   * be reactive we can use this json parse/stringify trick.
   */
  created() {
    this.hasLimitedPermissionsToUpdateGroup
      = !this.$can('update', 'group') && this.$can('update', 'group-users')
    if (this.hasLimitedPermissionsToUpdateGroup) {
      // If can't edit group settings, we dont show the first page
      this.currentStep = 1
      if (this.users.length === 0) {
        this.fetchUsers()
      }
    }

    this.initializeGroup()
  },
  methods: {
    /*
     * Fill all the fields if a group is being edited
     * If a group is either sms or none, these authenticators don't have
     * credentials, so the group authenticator only needs the type and name
     * If a group does have a credential assign it so it shows in the dropdown.
     */
    initializeGroup() {
      if (this.updateMode) {
        const {uuid, name, description, users, devices, notifyEmails, credential, authenticator}
          = this.group
        this.groupDetails = {
          uuid,
          name,
          description,
        }
        this.alertEmailsInput = this.formatEmails(notifyEmails)
        this.selectedUsers = this.deepCopy(users)
        this.selectedDevices = this.deepCopy(devices)
        if (credential) {
          this.groupAuthenticator = credential
        } else {
          this.groupAuthenticator = {
            type: authenticator || 'none',
            name: authenticator === 'sms' ? 'SMS Authentication' : 'Bypass',
            uuid: null,
          }
        }
      }
      this.fetchAuthenticators()
    },
    async fetchAuthenticators() {
      this.fetching = true
      try {
        const {credentialsList} = await this.listCredentialsQuery({
          page: 1,
          perPage: 100,
        })
        this.fetching = false
        this.setAuthenticatorList(credentialsList)
      } catch (error) {
        this.fetching = false
        this.displayAlert('fail', 'Unable to fetch authenticators.')
        console.log(error)
      }
    },
    setAuthenticatorList(credentialsList) {
      this.authenticators = [
        {
          name: 'Bypass',
          type: 'none',
          uuid: null,
        },
        {
          name: 'SMS Authentication',
          type: 'sms',
          uuid: null,
        },
        ...credentialsList,
      ]
    },
    setDeviceSearch() {
      this.devicePaginationParams = {
        ...this.devicePaginationParams,
        page: 1,
      }
    },
    setUserSearch() {
      this.userPaginationParams = {
        ...this.userPaginationParams,
        page: 1,
      }
    },
    closeModal() {
      this.$emit('close')
    },
    submissionStart() {
      const deviceGroup = (device) => {
        return device.group && device.group.uuid !== this.groupDetails.uuid
      }
      const conflictingDevice = this.selectedDevices.find(deviceGroup)

      if (conflictingDevice) {
        this.confirmation.description = `
          <p>
            Devices you have selected may already belong to other groups such as
            <strong>'${conflictingDevice.name}'</strong> in <strong>'${conflictingDevice.group.name}'</strong>.
            A device can only belong to one group.
          </p>
          <p>
            Would you like to re-assign these devices to this group?
          </p>
        `
        this.toggleConfirmModal()
      } else {
        this.submit()
      }
    },
    confirmAction() {
      this.toggleConfirmModal()
      this.submit()
    },
    submit() {
      const {uuid, name, description} = this.groupDetails
      const _payload = {
        uuid,
        name,
        description,
        authenticator: this.groupAuthenticator,
        emails: this.alertEmails,
        addUsers: this.usersByUsername(this.addedUsers),
        deleteUsers: this.usersByUsername(this.deletedUsers),
        addDevices: this.devicesByAddress(this.addedDevices),
        deleteDevices: this.devicesByAddress(this.deletedDevices),
      }
      this.$emit('submit', _payload)
    },
    /*
     * Backend needs an array of the device's mac addresses
     */
    devicesByAddress(devices = []) {
      return devices.map((device) => {
        return device.serialNumber
      })
    },
    /*
     * Backend needs an array of the usernames
     */
    usersByUsername(users = []) {
      return users.map((user) => {
        return user.username
      })
    },
    toNextStep() {
      const stepNumber = this.determineNextStep()
      if (this.canProceedToStep(stepNumber)) {
        this.currentStep = stepNumber
      }
    },
    determineNextStep() {
      return this.hasLimitedPermissionsToUpdateGroup ? 3 : this.currentStep + 1
    },
    canProceedToStep(stepNumber) {
      this.errorMessage = ''

      switch (stepNumber) {
      case 1:
        if (this.canDetailsProceed()) {
          this.ensureUsersLoaded()
          return true
        }
        return false
      case 2:
        this.ensureDevicesLoaded()
        return true
      default:
        return true
      }
    },
    ensureDevicesLoaded() {
      if (this.devices.length === 0) {
        this.fetchDevices()
      }
    },
    ensureUsersLoaded() {
      if (this.users.length === 0) {
        this.fetchUsers()
      }
    },
    toBackStep() {
      let stepNumber = 0
      if (this.hasLimitedPermissionsToUpdateGroup) {
        // When limited access user, the only back step is page 1 - users udpate
        stepNumber = 1
      } else {
        stepNumber = this.currentStep - 1
      }
      this.currentStep = stepNumber
    },
    canDetailsProceed() {
      const {description, name} = this.groupDetails
      if (this.isEmptyString(description) || !this.isAlphaNumeric(name)) {
        this.errorMessage = 'Please enter a valid name and description.'
        return false
      }

      if (this.alertEmails.length !== this.alertEmailsInput.length) {
        this.errorMessage = 'Invalid email address.'
        return false
      }

      return true
    },
    async fetchUsers() {
      this.fetching = true
      try {
        const _result = await this.listUsersQuery(
          this.userPaginationParams,
          this.userSearchTerm,
          true,
        )
        const {userList, totalRows} = _result
        this.fetching = false
        this.totalUserRows = totalRows
        this.users = userList
          .filter((user) => {
            return user.username !== 'bypassUser'
          })
          .map((user) => {
            return {
              ...user,
              inactiveMessage: user.isActive ? '' : 'Inactive',
            }
          })
        this.users.forEach((user) => {
          user.groupNames = user.groups
            .map((group) => {
              return group.name
            })
            .join(', ')
        })
      } catch (error) {
        this.fetching = false
        this.displayAlert('fail', 'Unable to retrieve users.')
        console.log('Fetch Users Failed.', error)
      }
    },
    selectedUserChange(selections) {
      this.selectedUsers = selections
    },
    /*
     * On the review screen, remove user from selection (un-add) or
     * add user back to selection (un-delete) if they're not selected
     */
    deselectListUser(user) {
      const _userIndex = this.selectedUsers.indexOf(user)
      if (_userIndex === -1) {
        this.selectedUsers.push(user)
      } else {
        this.selectedUsers.splice(_userIndex, 1)
      }
    },
    async fetchDevices() {
      const selectedCallback = (device) => {
        // Show a confirmation modal if the device is already in a group
        if (device.group) {
          this.displayAlert('fail', 'Warning: Device selected is already in a group.')
        }
        return true
      }

      this.fetching = true
      try {
        const _result = await this.listDevicesQuery(
          this.devicePaginationParams,
          this.deviceSearchTerm,
        )
        this.fetching = false
        const {deviceList, totalRows} = _result
        this.totalDeviceRows = totalRows
        this.devices = deviceList
        this.devices.forEach((device) => {
          device.groupName = device.group ? device.group.name : ''
          device.selected = false
          device.callback = selectedCallback
        })
      } catch (error) {
        this.fetching = false
        this.displayAlert('fail', 'Unable to retrieve devices.')
        console.log('Fetch Devices Failed.', error)
      }
    },
    toggleConfirmModal() {
      this.showConfirmModal = !this.showConfirmModal
    },
    selectedDeviceChange(selections) {
      this.selectedDevices = selections
    },
    deselectListDevice(device) {
      const _deviceIndex = this.selectedDevices.indexOf(device)
      if (_deviceIndex === -1) {
        this.selectedDevices.push(device)
      } else {
        this.selectedDevices.splice(_deviceIndex, 1)
      }
    },
    /*
     * Compare the two arrays of objects checking whether at least one of the
     * items in one array is in the other based on the passed key value.
     */
    deletedItems(originalItems, selectedItems, param) {
      const _deletedItems = originalItems.filter((obj) => {
        return !selectedItems.some((obj2) => {
          return obj[param] === obj2[param]
        })
      })
      return _deletedItems
    },
    addedItems(originalItems, selectedItems, param) {
      const _addedItems = selectedItems.filter((obj) => {
        return !originalItems.some((obj2) => {
          return obj[param] === obj2[param]
        })
      })
      return _addedItems
    },
    emailsChanged(newEmails) {
      this.alertEmailsInput = newEmails
    },
    formatEmails(emails) {
      if (emails.length > 0) {
        return emails.split(',').map((email) => {
          return {
            text: email,
            tiClasses: ['ti-valid'],
          }
        })
      }
      return []
    },
  },
}
</script>

<style scoped lang="scss">
.group {
  &-list {
    list-style-type: none;
    padding-inline-start: 0;
    max-height: 400px;
    overflow: auto;
    &-container {
      border-radius: 5px;
      padding: 1em;
      border: 1px solid $light-grey;
      padding: 1em;
      box-sizing: border-box;
      margin-bottom: 1em;
      overflow-wrap: break-word;
    }
    &-item {
      @include flex-display();
      @include flex-align-items(center);
      @include flex-justify-content(space-between);
      padding: 0.3em;
      border-bottom: 1px solid $light-grey;
      &:last-child {
        border-bottom: none;
      }
      &-remove {
        @include flex-display();
        @include flex-align-items(center);
        margin-left: 0.5em;
        transition: transform 0.3s;
        &:hover {
          cursor: pointer;
          transform: scale(1.3);
        }
      }
    }
  }
}
</style>
